import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import MDSpinner from 'react-md-spinner';
import ShowtimesDateFilter from '../../ShowtimesDateFilter';
import styled from 'styled-components';
import ChoicesFilter from '../../ChoicesFilter';
import { MovieType } from '../../../types/entities';
import FavoriteCinemasShowtimesBlock from './FavoriteCinemasShowtimesBlock';
import AreaCinemasShowtimesBlock from './AreaCinemasShowtimesBlock';
import { getBootstrapFilters, isUserLogged } from '../../../reducers';
import { getLocationIfGranted, PositionType } from '../../../utils/location';
import { getUserFavoriteCinemasIds } from '../../../reducers/user';

const FiltersCotainer = styled.div`
  background: var(--bg-secondary);

  body.theme-market & { background: var(--page-bg); border-bottom: 1px solid var(--text-primary); }
  body.theme-platinum & { background: var(--primary-c); }
`;

interface Props {
    attributes: any;
    area: { type: 'state' | 'area', id: number } | undefined;
    data: {
        faves: { data: any, loading: boolean },
        area: { data: any, loading: boolean },
    };
    favCinemasIds?: number[];
    isLoading?: boolean;
    isUserLogged: boolean;
    loadError?: boolean;
    dates?: Array<string>;
    formats?: Array<string>;
    languages?: Array<string>;
    movie: MovieType;
    onAreaSelectorOpen: () => void;
    onDateChange?: (date: string) => void;
    onFavCinemaToggle?: (cinemaId: number, isFave: boolean) => void;
    onFavesSelectorOpen: () => void;
    onFormatToggle?: (format: string) => void;
    onLanguageToggle?: (lang: string) => void;
    clearFilters?: () => void;
}

const ShowtimesPanel = (props: Props) => {
    const [selectedDate, setSelectedDate] = React.useState(props.dates && props.dates.length > 0 ? props.dates[0] : undefined);
    const [filtersSelection, setFiltersSelection] = React.useState({
        format: [],
        lang: [],
    });
    const [favCinemasIds, setFavCinemasIds] = React.useState<number[] | undefined>(props.favCinemasIds || undefined);
    const [userLocation, setUserLocation] = React.useState<PositionType | null>(null);

    useEffect(() => {
        getLocationIfGranted().then(location => {
            if (location) {
                setUserLocation(location);
            }
        });
    }, []);

    useEffect(() => {
        if (!props.data.faves || !props.data.faves.data || !props.data.faves.data.cinemas || !props.data.faves.data.cinemas.length) {
            setFavCinemasIds([]);
            return;
        }

        setFavCinemasIds(props.data.faves.data.cinemas.map(cinema => cinema.cinema.id));
    }, [props.data]);

    useEffect(() => {
        if (props.dates && props.dates.length > 0) {
            if (!selectedDate || !props.dates.includes(selectedDate)) {
                setSelectedDate(props.dates[0]);
            }
        }
    }, [props.dates, selectedDate]);

    useEffect(() => {
        setFiltersSelection({
            format: [],
            lang: [],
        });
    }, [props.languages, props.formats]);

    function onFilterSet(key, selection) {
        setFiltersSelection({
            ...filtersSelection,
            [key]: selection,
        });
    }
    
    function onCinemaFave(cinemaId: number, isFave: boolean) {
        if (isFave) {
            setFavCinemasIds(favCinemasIds ? [...favCinemasIds, cinemaId] : [cinemaId]);
        } else {
            setFavCinemasIds(favCinemasIds ? favCinemasIds.filter(id => id !== cinemaId) : []);
        }
        props.onFavCinemaToggle && props.onFavCinemaToggle(cinemaId, isFave);
    }

    const areAllBlocksLoading = Object.values(props.data).some((block: { loading: boolean }) => block.loading);

    if (areAllBlocksLoading) {
        return <div className="container py-5 text-center"><MDSpinner singleColor="var(--primary)" /></div>;
    }

    if (props.loadError) {
        return <div className="container py-5">Ocurrió un error</div>;
    }

    return (
        <React.Fragment>
            {props.dates && props.dates.length > 0 && selectedDate ? (
                <>
                    <ShowtimesDateFilter dates={props.dates} date={selectedDate} onDateSelect={(date) => setSelectedDate(date)} />
                    <FiltersCotainer>
                        <ChoicesFilter
                            collapsible={true}
                            label="Formato"
                            partialSet={true}
                            filters={props.formats}
                            selection={filtersSelection.format}
                            attributes={props.attributes}
                            onSave={(selection) => onFilterSet('format', selection)}
                        />
                        <ChoicesFilter
                            collapsible={true}
                            label="Idioma"
                            partialSet={true}
                            filters={props.languages}
                            selection={filtersSelection.lang}
                            attributes={props.attributes}
                            onSave={(selection) => onFilterSet('lang', selection)}
                        />
                    </FiltersCotainer>
                </>
            ) : <span style={{borderBottom: '1px solid var(--text-primary)', display: 'block', height: '1px'}}></span>}
            
            <FavoriteCinemasShowtimesBlock
                {...props.data.faves}
                isUserLogged={props.isUserLogged}
                attributes={props.attributes}
                formats={props.formats}
                languages={props.languages}
                filters={filtersSelection}
                date={selectedDate}
                movie={props.movie}
                favCinemasIds={favCinemasIds}
                onFavesSelectorOpen={props.onFavesSelectorOpen}
                onCinemaFave={onCinemaFave}
                userLocation={userLocation}
            />
            <AreaCinemasShowtimesBlock
                {...props.data.area}
                area={props.area}
                attributes={props.attributes}
                formats={props.formats}
                languages={props.languages}
                filters={filtersSelection}
                date={selectedDate}
                movie={props.movie}
                onAreaSelectorOpen={props.onAreaSelectorOpen}
                favCinemasIds={favCinemasIds}
                onCinemaFave={onCinemaFave}
                userLocation={userLocation}
            />
        </React.Fragment>
    );
}

const mapStateToProps = (state, props) => {
    return {
        attributes: getBootstrapFilters(state),
        favCinemasIds: getUserFavoriteCinemasIds(state),
        isUserLogged: isUserLogged(state),
    }
};

export default connect(
    mapStateToProps
)(ShowtimesPanel);